import { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import {
  Button,
  Input,
  Label,
  Switch,
  Dialog,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogContent,
  DialogFooter,
} from '@/components/ui';
import { departmentFormSchema, type DepartmentFormValues, generateSlug } from '@/lib/zod-schemas';
import { useDepartmentActions } from '@/lib/admin-store';
import type { Department } from '@/lib/admin-types';

interface DepartmentFormDialogProps {
  open: boolean;
  department?: Department | null;
  onClose: () => void;
}

/**
 * Modal de criar/editar departamento.
 *
 * Robustez:
 *   - Slug auto-gerado a partir do nome (mas editável)
 *   - Validação Zod com regex pra slug válido
 *   - Loading state, lock durante submit
 *   - Aviso quando edita: trocar slug pode quebrar links existentes
 */
export function DepartmentFormDialog({ open, department, onClose }: DepartmentFormDialogProps) {
  const isEdit = !!department;
  const { createDepartment, updateDepartment, pending } = useDepartmentActions();

  const {
    register,
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isSubmitting, isDirty, dirtyFields },
  } = useForm<DepartmentFormValues>({
    resolver: zodResolver(departmentFormSchema),
    defaultValues: department
      ? {
          nome: department.nome,
          slug: department.slug,
          descricao: department.descricao,
          ordem: department.ordem,
          ativo: department.ativo,
        }
      : {
          nome: '',
          slug: '',
          descricao: '',
          ordem: 99,
          ativo: true,
        },
  });

  const nome = watch('nome');
  const slug = watch('slug');

  // Auto-gera slug do nome (só quando criando — não interfere se usuário editou manualmente)
  useEffect(() => {
    if (!isEdit && nome && !dirtyFields.slug) {
      setValue('slug', generateSlug(nome));
    }
  }, [nome, isEdit, dirtyFields.slug, setValue]);

  async function onSubmit(values: DepartmentFormValues) {
    if (isEdit && department) {
      await updateDepartment(department.id, values);
    } else {
      await createDepartment(values);
    }
    onClose();
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()} size="md">
      <DialogHeader>
        <DialogTitle>{isEdit ? 'Editar departamento' : 'Novo departamento'}</DialogTitle>
        <DialogDescription>
          {isEdit
            ? `Atualizar as informações de ${department.nome}.`
            : 'Criar uma nova área da empresa no sistema.'}
        </DialogDescription>
      </DialogHeader>

      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <DialogContent>
          <div className="space-y-4">
            <div>
              <Label htmlFor="nome" required>Nome</Label>
              <Input
                id="nome"
                autoFocus
                placeholder="Recursos Humanos"
                error={errors.nome?.message}
                {...register('nome')}
              />
            </div>

            <div>
              <Label htmlFor="slug" required>Identificador no endereço</Label>
              <Input
                id="slug"
                placeholder="recursos-humanos"
                error={errors.slug?.message}
                hint={
                  isEdit
                    ? 'Cuidado: trocar o identificador quebra os links antigos. Só mude se precisar mesmo.'
                    : 'Criado automaticamente a partir do nome. Edite se precisar.'
                }
                {...register('slug')}
              />
              {slug && !errors.slug && (
                <p className="mt-1 break-all font-mono text-xs text-ink-3 dark:text-ink-dark-3">
                  Endereço: /menu/{slug}
                </p>
              )}
            </div>

            <div>
              <Label htmlFor="descricao">Descrição</Label>
              <Input
                id="descricao"
                placeholder="O que esse departamento faz?"
                error={errors.descricao?.message}
                {...register('descricao')}
              />
            </div>

            <div>
              <Label htmlFor="ordem">Posição no menu lateral</Label>
              <Input
                id="ordem"
                type="number"
                min={0}
                max={100}
                error={errors.ordem?.message}
                hint="Menor número aparece primeiro no menu. 0 = no topo."
                {...register('ordem', { valueAsNumber: true })}
              />
            </div>

            <div className="rounded-xl bg-paper-warm p-4 dark:bg-paper-dark-warm">
              <Controller
                name="ativo"
                control={control}
                render={({ field }) => (
                  <Switch
                    label="Departamento em uso"
                    hint="Desligado: ninguém consegue acessar, mas nada é apagado"
                    checked={field.value}
                    onChange={(e) => field.onChange(e.target.checked)}
                  />
                )}
              />
            </div>
          </div>
        </DialogContent>

        <DialogFooter>
          <Button type="button" variant="ghost" onClick={onClose} disabled={isSubmitting}>
            Cancelar
          </Button>
          <Button
            type="submit"
            loading={isSubmitting || pending === 'create' || (isEdit && pending === department?.id)}
            disabled={isEdit && !isDirty}
          >
            {isEdit ? 'Salvar alterações' : 'Criar departamento'}
          </Button>
        </DialogFooter>
      </form>
    </Dialog>
  );
}
